import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { productPageData, companyInfo } from '@/mocks/home';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://nocodb.aibrick.co.kr';
const XC_TOKEN = import.meta.env.VITE_XC_TOKEN || 'FIxhc3kiKgR7TVnzUX8LyXARQSdTjJt0U5AcrOff'; // NocoDB에서 발급받은 API 토큰을 기입해 주세요.
const NOCODB_API_URL = `${API_BASE_URL}/api/v3/data/p32e7pagy9rr8w4/mxbg7xtdv44lmyp/records`;

interface LoanProductPageProps {
  slug: string;
}

export default function LoanProductPage({ slug }: LoanProductPageProps) {
  const data = productPageData[slug];
  const navigate = useNavigate();

  // Scroll to top on page mount/slug change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const productValueMap: Record<string, string> = {
    personal: 'personal',
    corporate: 'corporate',
    'self-employed': 'self',
    enterprise: 'enterprise',
    daily: 'daily',
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setFormStatus('loading');
      setFormError('');

      const form = e.currentTarget;
      const formData = new FormData(form);

      const honeypot = (formData.get('website_alt') as string || '').trim();
      if (honeypot) {
        setFormStatus('success');
        form.reset();
        setAgreed(false);
        return;
      }

      if (!agreed) {
        setFormError('개인정보취급방침에 동의해 주세요.');
        setFormStatus('error');
        return;
      }

      if (XC_TOKEN === 'YOUR_NOCODB_XC_TOKEN') {
        setFormError('서버 설정오류: NocoDB API 토큰이 설정되지 않았습니다.');
        setFormStatus('error');
        return;
      }

      try {
        const name = (formData.get('name') as string || '').trim();
        const phone = (formData.get('phone') as string || '').trim();
        const amount = formData.get('amount') as string || '';
        const product = formData.get('product') as string || '';

        const response = await fetch(NOCODB_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'xc-token': XC_TOKEN
          },
          body: JSON.stringify({
            fields: { name, phone, amount, product }
          })
        });

        if (response.ok) {
          setFormStatus('success');
          form.reset();
          setAgreed(false);
        } else {
          const errText = await response.text();
          console.error('NocoDB Error:', errText);
          setFormError('상담 신청 등록에 실패했습니다. 다시 시도해 주세요.');
          setFormStatus('error');
        }
      } catch (err) {
        console.error('Network Error:', err);
        setFormError('네트워크 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
        setFormStatus('error');
      }
    },
    [agreed]
  );

  if (!data) return null;

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative w-full h-[420px] md:h-[520px] overflow-hidden">
          <img
            src={data.heroImage}
            alt={data.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-primary-700/75" />
          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-8 lg:px-12">
              <div className="max-w-2xl">
                <p className="text-sm font-label font-medium text-white/80 mb-3 tracking-wider">
                  {companyInfo.name} 대출상품
                </p>
                <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4">
                  {data.title}
                </h1>
                <p className="text-base md:text-lg text-white/90 font-label mb-6">
                  {data.subtitle}
                </p>
                <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent-500 text-white font-label font-bold text-sm">
                  <i className="ri-coins-line w-4 h-4 flex items-center justify-center" />
                  {data.maxAmount}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Description */}
        <section className="w-full py-12 md:py-16 bg-white">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <p className="text-sm md:text-base text-foreground-700 font-label leading-relaxed max-w-3xl">
              {data.description}
            </p>
          </div>
        </section>

        {/* Features */}
        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
              <span className="text-primary-500">{data.title}</span>의 특별한 장점
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {data.features.map((f, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl p-6 md:p-8 border border-background-200/70"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center mb-4">
                    <i
                      className={`${f.icon} w-6 h-6 flex items-center justify-center text-primary-500 text-xl`}
                    />
                  </div>
                  <h3 className="font-heading text-base md:text-lg font-bold text-foreground-900 mb-2">
                    {f.title}
                  </h3>
                  <p className="text-sm text-foreground-600 font-label">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Conditions + Docs */}
        <section className="w-full py-12 md:py-16 bg-white">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 max-w-4xl">
              <div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-5">
                  신청 조건
                </h3>
                <ul className="space-y-3">
                  {data.conditions.map((c, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-600 text-sm" />
                      </span>
                      <span className="text-sm text-foreground-700 font-label">{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-5">
                  필요 서류
                </h3>
                <ul className="space-y-3">
                  {data.requiredDocs.map((d, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-file-list-line w-4 h-4 flex items-center justify-center text-accent-600 text-sm" />
                      </span>
                      <span className="text-sm text-foreground-700 font-label">{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Consult Form */}
        <section id="consult" className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8">
                {data.title} 상담 신청하기
              </h2>
              <div className="bg-white rounded-2xl md:rounded-3xl border border-background-200/70 p-6 md:p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary-50 rounded-full -translate-y-1/2 translate-x-1/2" />
                <form data-readdy-form id={`loan-consult-form-${slug}`} onSubmit={handleSubmit} className="space-y-4 relative z-10">
                  <div className="honeypot-field">
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <div>
                    <label className="block text-sm font-label font-semibold text-foreground-800 mb-1.5">
                      성함(대표자명) <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text" name="name" placeholder="이름을 입력해주세요." maxLength={10} required
                      className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-sm font-label text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-label font-semibold text-foreground-800 mb-1.5">
                      연락처(-없이) <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="tel" name="phone" placeholder="ex) 01012345678" inputMode="numeric" required pattern="[0-9]{10,11}"
                      className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-sm font-label text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-label font-semibold text-foreground-800 mb-1.5">신청금액</label>
                    <select name="amount" required className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-sm font-label text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all appearance-none cursor-pointer">
                      <option value="">필요하신 금액을 선택해주세요</option>
                      <option value="1000">1,000만원 이상~</option>
                      <option value="3000">3,000만원 이상~</option>
                      <option value="5000">5,000만원 이상~</option>
                      <option value="10000">1억원 이상~</option>
                      <option value="100000">10억원 이상~</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-label font-semibold text-foreground-800 mb-1.5">대출상품</label>
                    <select name="product" required defaultValue={productValueMap[slug]} className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-sm font-label text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all appearance-none cursor-pointer">
                      <option value="">원하시는 상품을 선택해주세요</option>
                      <option value="enterprise">기업단기대출</option>
                      <option value="corporate">법인사업자대출</option>
                      <option value="personal">개인사업자대출</option>
                      <option value="self">자영업자대출</option>
                      <option value="daily">일수대출</option>
                    </select>
                  </div>
                  <div className="flex items-start gap-2 pt-2">
                    <input type="checkbox" id={`agree-${slug}`} checked={agreed} onChange={(e) => setAgreed(e.target.checked)} required className="mt-1 w-4 h-4 rounded border-background-300/60 text-primary-500 focus:ring-primary-400 cursor-pointer" />
                    <label htmlFor={`agree-${slug}`} className="text-xs font-label text-foreground-600 leading-relaxed cursor-pointer">
                      개인정보취급방침 동의함{' '}
                      <button type="button" onClick={() => document.getElementById('policy-modal')?.classList.remove('hidden')} className="text-primary-500 underline hover:text-primary-600">[내용보기]</button>
                    </label>
                  </div>
                  {formStatus === 'error' && formError && (
                    <div className="p-3 rounded-xl bg-red-50 text-red-600 text-sm font-label">{formError}</div>
                  )}
                  {formStatus === 'success' && (
                    <div className="p-3 rounded-xl bg-primary-50 text-primary-700 text-sm font-label">상담 신청이 완료되었습니다. 전문 상담원이 빠른 시일 내에 연락드리겠습니다.</div>
                  )}
                  <button type="submit" disabled={formStatus === 'loading'} className="w-full py-4 rounded-xl bg-primary-500 text-white font-label font-semibold text-sm md:text-base hover:bg-primary-600 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap">
                    {formStatus === 'loading' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line w-5 h-5 flex items-center justify-center animate-spin" />
                        처리 중...
                      </span>
                    ) : '무료 상담 신청하기'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full py-12 md:py-16 bg-white">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
              <span className="text-primary-500">{data.title}</span> 자주 묻는 질문
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {data.faqs.map((faq, i) => (
                <div key={i} className="bg-background-100 rounded-xl md:rounded-2xl border border-background-200/70 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center gap-3 md:gap-4 p-5 md:p-6 text-left">
                    <span className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-label font-bold text-sm">Q</span>
                    </span>
                    <strong className="flex-1 text-sm md:text-base font-label font-semibold text-foreground-900">{faq.question}</strong>
                    <i className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-foreground-400 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-48' : 'max-h-0'}`}>
                    <div className="px-5 md:px-6 pb-5 md:pb-6 pl-16 md:pl-20">
                      <p className="text-sm text-foreground-600 font-label leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Other Products */}
        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
              다른 대출 상품도 확인해 보세요
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-4xl mx-auto">
              {Object.entries(productPageData)
                .filter(([key]) => key !== slug)
                .slice(0, 4)
                .map(([key, p]) => (
                  <button
                    key={key}
                    onClick={() => navigate(`/${key}`)}
                    className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 border border-background-200/70 hover:border-primary-300 hover:shadow-md transition-all duration-300 text-left"
                  >
                    <h3 className="font-heading text-sm md:text-base font-bold text-foreground-900 mb-1">{p.title}</h3>
                    <p className="text-xs text-foreground-500 font-label">{p.maxAmount}</p>
                  </button>
                ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}